import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Fragment, ReactNode, useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import RadioButtons from "./radio-buttons";
import { ProductType, Variant } from "interfaces/products";
import {
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "./ui/drawer";
import cart from "observables/cart";
import { performAuthenticatedCall } from "lib/utils";
import useStoreId from "hooks/use-store-id";
import CataloguePicture from "./catalogue-picture";

interface Props {
  comboId: number;
  comboName: string;
  comboPriceAfterVat: number;
  onCloseDrawer: () => void;
  isComboDrawerOpen: boolean;
  onClickAddCombo: (variantIds: Array<number>) => void;
}

interface Element {
  id: number;
  variant: Variant;
  product?: {
    id: number;
    name: string;
    type: {
      id: number;
      name: string;
    };
    variants: Array<Variant>;
  };
  productType?: {
    id: number;
    name: string;
    products: Array<{
      id: number;
      name: string;
      variants: Array<Variant>;
      type: ProductType;
    }>;
  };
  comboCustomList?: {
    id: number;
    name: string;
    items: Array<{
      id: number;
      product: {
        id: number;
        name: string;
        type: {
          id: number;
          name: string;
        };
        variants: Array<Variant>;
      };
      variant: Variant;
      productType: {
        id: number;
        name: string;
        products: Array<{
          id: number;
          name: string;
          type: ProductType;
          variants: Array<Variant>;
        }>;
      };
    }>;
  };
}

const AddComboOrderDrawerContent = ({
  comboId,
  comboName,
  comboPriceAfterVat,
  onCloseDrawer,
  isComboDrawerOpen,
  onClickAddCombo,
}: Props) => {
  const variantIds = useRef<Array<number>>([]);
  const selectedVariantsRef = useRef<Record<string, number>>({});

  const [combo, setCombo] = useState<{
    elements: Array<Element>;
  }>();

  const { storeId } = useStoreId();

  const [selectedVariants, setSelectedVariants] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    const initCombo = async () => {
      const combo = await performAuthenticatedCall<{
        elements: Array<Element>;
      }>(`/stores/${storeId}/combos/${comboId}/elements`, "", "GET");

      setCombo(combo);
    };

    if (!isComboDrawerOpen) return;

    initCombo();
  }, [comboId, isComboDrawerOpen]);

  if (!combo) return null;

  const getVariant = (element: Element, variantId: number) => {
    if (element?.variant && Number(element.variant.id) === Number(variantId)) {
      return element.variant;
    }

    if (element?.product) {
      return element?.product?.variants.find(
        (e) => Number(e.id) === Number(variantId)
      );
    }

    if (element?.productType?.products) {
      return element?.productType?.products
        .flatMap((e) => e.variants)
        .find((e) => Number(e.id) === Number(variantId));
    }

    if (!element?.comboCustomList) return;

    for (const item of element?.comboCustomList.items) {
      let foundVariant: Variant;

      if (item.variant && Number(item.variant.id) === Number(variantId)) {
        foundVariant = item.variant;
      }

      if (item.product) {
        foundVariant = item.product.variants.find(
          (variant) => Number(variant.id) === Number(variantId)
        );
      }

      if (item.productType) {
        foundVariant = item.productType.products
          .flatMap((product) => product.variants)
          .find((variant) => Number(variant.id) === Number(variantId));
      }

      if (!foundVariant) continue;

      return foundVariant;
    }
  };

  const handleClickAddCombo = () => {
    const comboElements: Array<{
      itemId: number;
      name: string;
      quantity: number;
      comboName: string;
      amountAfterVat: number;
      comboId: number;
      comboElementId: number;
      productVariantId: number;
      group: number;
    }> = [];

    const pricePerComboElement =
      comboPriceAfterVat / Object.keys(selectedVariants).length;

    for (const elementId of Object.keys(selectedVariants)) {
      const variantId = selectedVariants[elementId];

      if (variantId === null) continue;

      const element = combo.elements.find(
        (e) => Number(e.id) === Number(elementId)
      );

      const variant = element ? getVariant(element, variantId) : null;

      if (!variant) continue;

      const itemId = new Date().getTime();

      cart.addElement({
        itemId,
        variantId: variant.id,
        variantName: variant.name,
        productName: variant.product.name,
        productType: variant.product.type,
        productId: variant.product.id,
        quantity: 1,
        priceAfterVatAfterPromotion: pricePerComboElement,
        priceAfterVatBeforePromotion: pricePerComboElement,
        comboId,
        comboElementId: Number(elementId),
        orderComboElement: {
          comboId,
          priceAfterVat: pricePerComboElement,
          group: Object.values(cart.combos).length + 1,
        },
      });

      comboElements.push({
        itemId,
        comboId,
        comboName,
        comboElementId: Number(elementId),
        productVariantId: variant.id,
        quantity: 1,
        amountAfterVat: pricePerComboElement,
        name: `${variant.product.name} ${variant.name}`,
        group: Object.values(cart.combos).length + 1,
      });
    }

    if (comboElements.length > 0) {
      cart.addCombo(comboElements);
      onClickAddCombo?.(variantIds.current);
    }

    setSelectedVariants({});
    onCloseDrawer();
  };

  const handleClickToggleVariant =
    (elementId: number, variantId: number) => async () => {
      const shouldRemove =
        Number(selectedVariantsRef.current[elementId]) === Number(variantId);

      selectedVariantsRef.current = shouldRemove
        ? {
            ...selectedVariantsRef.current,
            [elementId]: null,
          }
        : {
            ...selectedVariantsRef.current,
            [elementId]: variantId,
          };

      setSelectedVariants((prevSelectedVariants) => {
        return Number(prevSelectedVariants[elementId]) === Number(variantId)
          ? {
              ...prevSelectedVariants,
              [elementId]: null,
            }
          : {
              ...prevSelectedVariants,
              [elementId]: variantId,
            };
      });

      if (!shouldRemove) {
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_API_ORIGIN}/stores/${storeId}/catalogue/variants/${variantId}/extras`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json", // Adjust the content type as needed
              // Add any other headers if required
            },
          }
        );

        const _extras = await response.json();

        if (!_extras || _extras.length === 0) return;

        variantIds.current.push(variantId);

        return;
      }

      const _extras = [];
      let hasFiltered = false;
      for (const extra of variantIds.current) {
        if (hasFiltered) {
          _extras.push(extra);
          continue;
        }

        if (extra === variantId) {
          hasFiltered = true;
          continue;
        }

        _extras.push(extra);
      }

      variantIds.current = _extras;
    };

  const renderContent = (
    comboElements: Array<Element>,
    isComboList?: boolean
  ): ReactNode => {
    return (
      <>
        {comboElements.map((element: Element) => {
          if (element.comboCustomList) {
            return (
              <div key={element.id} className="grid gap-2">
                <h1 className="text-5xl">{element.comboCustomList.name}</h1>
                {renderContent(
                  element.comboCustomList.items.map((item) => ({
                    id: element.id,
                    variant: item.variant,
                    product: item.product,
                    productType: item.productType,
                  })),
                  true
                )}
              </div>
            );
          }

          if (element.variant) {
            return (
              <Fragment key={element.id}>
                {!isComboList && (
                  <h1 className="text-5xl">{element.variant.product.name}</h1>
                )}
                <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  <Card>
                    <CardHeader>
                      {isComboList && (
                        <CardTitle className="relative">
                          {element.variant.product.name}
                        </CardTitle>
                      )}
                      <CardDescription>
                        {element.variant.product.type.name}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="xl:flex justify-between">
                      <div className="overflow-auto h-36 grow">
                        <ul className="list-inside list-disc mb-2 text-sm">
                          {element.variant.product.items.map((item) => (
                            <li key={item.productItem.name}>
                              {item.productItem.name}
                            </li>
                          ))}

                          {element.variant.items.map((item) => (
                            <li key={item.productItem.name}>
                              {item.productItem.name}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <CataloguePicture
                        product={element.variant.product}
                        selectedVariant={element.variant}
                      />
                    </CardContent>

                    <CardFooter className="flex justify-center">
                      <div className="w-full">
                        <div className="m-1.5 text-center">
                          <Button
                            variant={
                              selectedVariants[element.id] ? "default" : "link"
                            }
                            onClick={handleClickToggleVariant(
                              element.id,
                              element.variant.id
                            )}
                          >
                            {element.variant.name}
                          </Button>
                        </div>
                      </div>
                    </CardFooter>
                  </Card>
                </div>
              </Fragment>
            );
          }

          if (element.product) {
            return (
              <Fragment key={element.id}>
                {!isComboList && (
                  <h1 className="text-5xl">{element.product.name}</h1>
                )}
                <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  <Card>
                    <CardHeader>
                      {isComboList && (
                        <CardTitle className="relative">
                          {element.product.name}
                        </CardTitle>
                      )}
                      <CardDescription>
                        {element.product.type.name}
                      </CardDescription>
                    </CardHeader>
                    <CardFooter className="flex justify-center">
                      <RadioButtons
                        onSelectValue={(variantId) => {
                          handleClickToggleVariant(
                            element.id,
                            Number(variantId)
                          )();
                        }}
                        options={element.product.variants.map((variant) => ({
                          value: String(variant.id),
                          displayName: variant.name,
                        }))}
                        selectedValue={String(selectedVariants[element.id])}
                      />
                    </CardFooter>
                  </Card>
                </div>
              </Fragment>
            );
          }

          if (!element.productType) return null;

          return (
            <Fragment key={element.id}>
              {!isComboList && (
                <h1 className="text-5xl">{element.productType.name}</h1>
              )}

              <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {element.productType.products.map((product) => (
                  <Card key={product.id}>
                    <CardHeader>
                      <CardTitle className="relative">{product.name}</CardTitle>
                      <CardDescription>{product.type.name}</CardDescription>
                    </CardHeader>
                    <CardFooter className="flex justify-center">
                      <RadioButtons
                        onSelectValue={(variantId) => {
                          handleClickToggleVariant(
                            element.id,
                            Number(variantId)
                          )();
                        }}
                        options={product.variants.map((variant) => ({
                          value: String(variant.id),
                          displayName: variant.name,
                        }))}
                        selectedValue={String(selectedVariants[element.id])}
                      />
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </Fragment>
          );
        })}
      </>
    );
  };

  return (
    <DrawerContent className="h-full">
      <DrawerHeader>
        <DrawerTitle>
          {"Formule «"}
          {comboName}
          {"»"}
        </DrawerTitle>
      </DrawerHeader>
      <DrawerDescription className="space-y-2 p-4 overflow-y-auto">
        {renderContent(combo.elements)}
      </DrawerDescription>
      <DrawerFooter>
        <div className="lg:text-right">
          <Button onClick={handleClickAddCombo} className="w-full lg:w-auto">
            {"Valider"}
          </Button>
        </div>
      </DrawerFooter>
    </DrawerContent>
  );
};

export default AddComboOrderDrawerContent;
